/**
 * Design System - Checkbox Component
 *
 * Accessible checkbox with label, description, and indeterminate state.
 *
 * @example
 * <Checkbox
 *   label="Accept terms and conditions"
 *   checked={accepted}
 *   onChange={(e) => setAccepted(e.target.checked)}
 * />
 *
 * @example
 * <Checkbox
 *   label="Enable notifications"
 *   description="Receive email notifications for important updates"
 *   checked={notifications}
 *   onChange={(e) => setNotifications(e.target.checked)}
 *   disabled
 * />
 */

import React, { forwardRef, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { cn } from '../utils/classNames';
import { FiCheck, FiMinus } from 'react-icons/fi';

const Checkbox = forwardRef(({
  label,
  description,
  checked = false,
  indeterminate = false,
  disabled = false,
  error,
  size = 'md',
  className,
  onChange,
  id,
  ...props
}, ref) => {
  const internalRef = useRef(null);
  const checkboxRef = ref || internalRef;

  // Generate unique ID if not provided
  const checkboxId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`;
  const descriptionId = description ? `${checkboxId}-description` : undefined;
  const errorId = error ? `${checkboxId}-error` : undefined;

  // Set indeterminate state (can't be done via props)
  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate, checkboxRef]);

  // Size styles
  const sizeStyles = {
    sm: {
      checkbox: 'h-4 w-4',
      icon: 'h-3 w-3',
      label: 'text-sm',
      description: 'text-xs',
    },
    md: {
      checkbox: 'h-5 w-5',
      icon: 'h-4 w-4',
      label: 'text-base',
      description: 'text-sm',
    },
    lg: {
      checkbox: 'h-6 w-6',
      icon: 'h-5 w-5',
      label: 'text-lg',
      description: 'text-base',
    },
  };

  // Base checkbox styles
  const checkboxStyles = cn(
    'relative flex-shrink-0 rounded border-2 transition-all duration-200 cursor-pointer',
    'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
    sizeStyles[size].checkbox,
    checked || indeterminate
      ? 'bg-primary-600 border-primary-600'
      : 'bg-white border-gray-300 hover:border-primary-500',
    disabled && 'opacity-50 cursor-not-allowed',
    error && !checked && !indeterminate && 'border-danger-500'
  );

  return (
    <div className={cn('flex items-start', className)}>
      {/* Hidden native checkbox */}
      <input
        ref={checkboxRef}
        type="checkbox"
        id={checkboxId}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        aria-invalid={!!error}
        aria-describedby={cn(
          descriptionId,
          errorId
        )}
        className="sr-only"
        {...props}
      />

      {/* Custom checkbox */}
      <label
        htmlFor={checkboxId}
        className={cn(
          'flex items-start gap-3',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer'
        )}
      >
        <span className={checkboxStyles}>
          {/* Check icon */}
          {checked && !indeterminate && (
            <FiCheck
              className={cn(
                'absolute inset-0 m-auto text-white',
                sizeStyles[size].icon
              )}
              aria-hidden="true"
            />
          )}

          {/* Indeterminate icon */}
          {indeterminate && (
            <FiMinus
              className={cn(
                'absolute inset-0 m-auto text-white',
                sizeStyles[size].icon
              )}
              aria-hidden="true"
            />
          )}
        </span>

        {/* Label and description */}
        {(label || description) && (
          <div className="flex-1 min-w-0">
            {label && (
              <span
                className={cn(
                  'font-medium text-gray-900',
                  sizeStyles[size].label,
                  disabled && 'text-gray-500'
                )}
              >
                {label}
              </span>
            )}
            {description && (
              <p
                id={descriptionId}
                className={cn(
                  'mt-0.5 text-gray-600',
                  sizeStyles[size].description,
                  disabled && 'text-gray-400'
                )}
              >
                {description}
              </p>
            )}
          </div>
        )}
      </label>

      {/* Error message */}
      {error && (
        <p
          id={errorId}
          role="alert"
          className="mt-1 text-sm text-danger-600"
        >
          {error}
        </p>
      )}
    </div>
  );
});

Checkbox.displayName = 'Checkbox';

Checkbox.propTypes = {
  label: PropTypes.string,
  description: PropTypes.string,
  checked: PropTypes.bool,
  indeterminate: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string,
  onChange: PropTypes.func,
  id: PropTypes.string,
};

export default Checkbox;
